// config/routes.ts
// Centralized route definitions

export const ROUTES = {
  // Staff routes (requires specific URL for staff access)
  LOGIN: '/staff',
  RESET_PASSWORD: '/reset-password',
  PUBLIC_TIMER: '/public-timer/:tournamentId',

  // Invite routes (for store/staff onboarding)
  INVITE: '/invite/:token',

  // Legal routes (public)
  PRIVACY_POLICY: '/privacy-policy',
  TERMS: '/terms',
  IMPRESSUM: '/impressum',
  BOT_PRIVACY: '/bot-privacy',

  // Guest routes (no auth required - simple code-based auth for visitors)
  GUEST_LOGIN: '/guest-login',
  GUEST_PORTAL: '/guest/tournament/:tournamentId',

  // Player Portal routes (player login is the default route)
  PLAYER_REGISTER: '/',
  PLAYER_DASHBOARD: '/player',
  PLAYER_TOURNAMENT: '/player/tournament/:tournamentId',
  PLAYER_SETTINGS: '/player/settings',
  PLAYER_FEEDBACK: '/player/feedback',
  PLAYER_DECKS: '/player/decks',
  PLAYER_DECK_NEW: '/player/decks/new',
  PLAYER_DECK_EDIT: '/player/decks/:deckId/edit',

  // Semi-public routes (optional auth)
  JOIN_TOURNAMENT: '/join/:tournamentId',

  // Protected routes (staff)
  LANDING: '/landing',
  FEEDBACK: '/feedback',

  // Post-login picker - users with both staff + player capability land
  // here and pick which dashboard to enter.
  WELCOME: '/welcome',

  // Tournament Dashboard (NEW)
  TOURNAMENT_DASHBOARD: '/tournament-dashboard',
  TABLE_MANAGEMENT_DASHBOARD: '/table-management-dashboard',
  DECK_CHECK_DASHBOARD: '/deck-check-dashboard',
  PLAYERS_DASHBOARD: '/players-dashboard',
  PROBLEM_TRACKER_DASHBOARD: '/problem-tracker-dashboard',

  // Tournament management
  TOURNAMENT_SETUP: '/tournament-setup',
  TOURNAMENT_SETUP_EDIT: '/tournament-setup/:tournamentId',
  ARCHIVED_TOURNAMENTS: '/archived-tournaments',

  // Tournament Series - overarching event with shared table capacity + staff
  TOURNAMENT_SERIES: '/tournament-series',
  TOURNAMENT_SERIES_NEW: '/tournament-series/new',
  TOURNAMENT_SERIES_DETAIL: '/tournament-series/:seriesId',
  // Magic-link to join a series staff pool. Auth required (anonymous
  // fine if you want guest-style accept) - registered as a protected
  // route so the user logs in first if they aren't.
  SERIES_INVITE: '/series-invite/:token',

  // Tournament Engine (NEW)
  TOURNAMENT_RUNNER: '/tournament/:tournamentId/run',
  TOURNAMENT_STANDINGS: '/tournament/:tournamentId/standings',
  TOURNAMENT_BRACKET: '/tournament/:tournamentId/bracket',

  // Matchmaking mode (PR #4 - on-demand queue-based sessions)
  MATCHMAKING_DASHBOARD: '/matchmaking-dashboard',
  MATCHMAKING_RUNNER: '/tournament/:tournamentId/matchmaking',

  // DQ Cases - disqualification case workflow
  DQ_CASES: '/dq-cases',
  DQ_CASE_NEW: '/dq-cases/new',
  DQ_CASE_DETAIL: '/dq-cases/:caseId',
  // Public magic-link page for participants (offender / witnesses)
  DQ_PARTICIPANT_FORM: '/dq/form/:token',

  // Streaming helpers — broadcast-side utilities for top-cut
  // displays, overlays, etc.
  STREAMING_TOP_CUT: '/streaming/top-cut',

  // Store delegation — store owners manage staff members who can
  // act as proxies for the store. STORE_INVITE_CLAIM is the
  // landing for the single-use invite link sent by store owners.
  STORE_MANAGERS: '/store-managers',
  STORE_INVITE_CLAIM: '/store-invite/:token',

  // Table management
  TABLE_MANAGEMENT: '/table-management/:tournamentId',

  // Player management
  VIEW_PLAYERS: '/view-players/:tournamentId',
  PLAYER_UPLOAD: '/player-upload/:tournamentId',

  // Deck Check
  DECK_CHECK: '/deck-check/:tournamentId',

  // Appeals
  APPEALS: '/appeals',
  APPEALS_LOG: '/appeals-log',

  // Penalties
  PENALTY_APPROVAL: '/penalty-approval',

  // Staff
  STAFF_MANAGEMENT: '/staff-management/:tournamentId',
  STAFF_DASHBOARD: '/staff-dashboard',
  STAFF_SETTINGS: '/staff-settings',
  STAFF_FLOOR_TEAMS: '/staff-floor-teams',
  CHAT: '/chat',
  STAFF_TOURNAMENT_ASSIGNMENTS: '/staff-tournament-assignments',
  STAFF_FEEDBACK: '/staff-feedback',
  FEEDBACK_REPORTS: '/feedback-reports',
  USER_SETTINGS: '/user-settings',

  // Timer
  TIMER_OVERVIEW: '/timer-overview',

  // Guided-tour sandbox (fake data; used by the in-app tutorial)
  TUTORIAL_TABLE: '/tutorial/table-management',
  TUTORIAL_DECKCHECK: '/tutorial/deck-check',
  TUTORIAL_TIMER: '/tutorial/timer-overview',
  TUTORIAL_JOBS: '/tutorial/job-board',
  TUTORIAL_DQ: '/tutorial/dq-cases',
  TUTORIAL_PENALTY: '/tutorial/penalty-approval',
  TUTORIAL_FEEDBACK: '/tutorial/staff-feedback',
  TUTORIAL_ROUNDARCHIVE: '/tutorial/round-archive',
  TUTORIAL_FLOORTEAMS: '/tutorial/floor-teams',
  TUTORIAL_SERIES: '/tutorial/tournament-series',
  TUTORIAL_CREATE: '/tutorial/create-tournament',
  TUTORIAL_APPEALS: '/tutorial/appeals',
  TUTORIAL_STAFFDASH: '/tutorial/staff-dashboard',
  TUTORIAL_TOURNSTAFF: '/tutorial/tournament-staff',
  TUTORIAL_JOBAPPS: '/tutorial/job-applications',
  TUTORIAL_STOREMGR: '/tutorial/store-managers',
  TUTORIAL_PLAYER: '/tutorial/player-dashboard',
  TUTORIAL_MATCHMAKING: '/tutorial/matchmaking',
  TUTORIAL_RUNNER: '/tutorial/tournament-runner',

  // Rounds
  ROUND_ARCHIVE: '/round-archive/:tournamentId',

  // Scorekeeper
  PROBLEM_TRACKER: '/problem-tracker/:tournamentId',

  // Admin
  ADMIN_DASHBOARD: '/admin-dashboard',
  ACCOUNTS: '/accounts',
  USER_APPROVAL: '/user-approval',
  PLAYER_ACCOUNTS: '/player-accounts',
  RETENTION_POLICY: '/retention-policy',
  STORE_INVITES: '/store-invites',
  STORE_MANAGEMENT: '/store-management',
  EVENT_CODES: '/admin/event-codes',

  // Job Board
  JOB_BOARD: '/jobs',
  STORE_APPLICATIONS: '/store/applications',
  MY_APPLICATIONS: '/my-applications',

  // Stream Overlay Manager routes
  STREAM_MANAGER: '/stream',
  STREAM_OPERATOR: '/stream/:sessionId/operator',
  STREAM_CASTER: '/stream/:sessionId/caster',
  STREAM_SIMPLE: '/stream/:sessionId/simple',
  STREAM_EDITOR: '/stream/:sessionId/editor',
  STREAM_OVERLAY: '/stream/:sessionId/overlay',

  // Parent Account routes
  PARENT_DASHBOARD: '/parent',
  PARENT_CHILD_DETAILS: '/parent/child/:childId',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = (typeof ROUTES)[RouteKey];

// Route helpers
export const routes = {
  invite: (token: string): string => `/invite/${token}`,
  publicTimer: (tournamentId: string): string => `/public-timer/${tournamentId}`,
  joinTournament: (tournamentId: string): string => `/join/${tournamentId}`,
  guestPortal: (tournamentId: string): string => `/guest/tournament/${tournamentId}`,
  tournamentSetup: (tournamentId?: string): string => tournamentId ? `/tournament-setup/${tournamentId}` : '/tournament-setup',
  tableManagement: (tournamentId: string): string => `/table-management/${tournamentId}`,
  viewPlayers: (tournamentId: string): string => `/view-players/${tournamentId}`,
  playerUpload: (tournamentId: string): string => `/player-upload/${tournamentId}`,
  staffManagement: (tournamentId: string): string => `/staff-management/${tournamentId}`,
  roundArchive: (tournamentId: string): string => `/round-archive/${tournamentId}`,
  deckCheck: (tournamentId: string): string => `/deck-check/${tournamentId}`,
  problemTracker: (tournamentId: string): string => `/problem-tracker/${tournamentId}`,

  // Tournament Engine routes (NEW)
  tournamentRunner: (tournamentId: string): string => `/tournament/${tournamentId}/run`,
  tournamentStandings: (tournamentId: string): string => `/tournament/${tournamentId}/standings`,
  tournamentBracket: (tournamentId: string): string => `/tournament/${tournamentId}/bracket`,

  // Matchmaking mode
  matchmakingDashboard: (): string => '/matchmaking-dashboard',
  matchmakingRunner: (tournamentId: string): string => `/tournament/${tournamentId}/matchmaking`,

  // DQ Cases
  dqCases: (): string => '/dq-cases',
  dqCaseNew: (): string => '/dq-cases/new',
  dqCaseDetail: (caseId: string): string => `/dq-cases/${caseId}`,
  dqParticipantForm: (token: string): string => `/dq/form/${token}`,

  // Tournament Series
  tournamentSeries: (): string => '/tournament-series',
  tournamentSeriesNew: (): string => '/tournament-series/new',
  tournamentSeriesDetail: (seriesId: string): string => `/tournament-series/${seriesId}`,
  seriesInvite: (token: string): string => `/series-invite/${token}`,

  // Player Portal routes
  playerTournament: (tournamentId: string): string => `/player/tournament/${tournamentId}`,
  playerDecks: (): string => '/player/decks',
  playerDeckNew: (): string => '/player/decks/new',
  playerDeckEdit: (deckId: string): string => `/player/decks/${deckId}/edit`,

  // Stream routes
  streamOperator: (sessionId: string): string => `/stream/${sessionId}/operator`,
  streamCaster: (sessionId: string): string => `/stream/${sessionId}/caster`,
  streamSimple: (sessionId: string): string => `/stream/${sessionId}/simple`,
  streamEditor: (sessionId: string): string => `/stream/${sessionId}/editor`,
  streamOverlay: (sessionId: string): string => `/stream/${sessionId}/overlay`,

  // Parent routes
  parentChildDetails: (childId: string): string => `/parent/child/${childId}`,
};

// Account types (global)
export type AccountType = 'admin' | 'store' | 'staff' | 'player' | 'user';

// Tournament roles (context-specific per tournament)
export type TournamentRole =
  | 'Manager'
  | 'Head Judge'
  | 'Scorekeeper'
  | 'Team Lead'
  | 'Floor Judge'
  | 'Feature Match'
  | 'Shield'
  | 'Deck Check'
  | 'Stream';

// Legacy role type for backwards compatibility
export type UserRole = 'admin' | TournamentRole;

// Route access configuration
interface RouteAccess {
  // Account types that can access this route (global check)
  accountTypes?: AccountType[];
  // Tournament roles that can access this route (tournament-specific check)
  tournamentRoles?: TournamentRole[];
  // If true, any authenticated user can access
  anyAuthenticated?: boolean;
}

// Role-based route access
// Routes not listed are open to all staff by default
export const ROUTE_ACCESS: Record<string, RouteAccess> = {
  // Admin-only routes
  [ROUTES.ADMIN_DASHBOARD]: { accountTypes: ['admin'] },
  [ROUTES.ACCOUNTS]: { accountTypes: ['admin'] },
  [ROUTES.USER_APPROVAL]: { accountTypes: ['admin'], tournamentRoles: ['Manager'] },
  [ROUTES.PLAYER_ACCOUNTS]: { accountTypes: ['admin'] },
  [ROUTES.RETENTION_POLICY]: { accountTypes: ['admin'] },
  [ROUTES.STORE_MANAGEMENT]: { accountTypes: ['admin'] },
  [ROUTES.EVENT_CODES]: { accountTypes: ['admin'] },

  // Manager/Head Judge routes
  [ROUTES.PENALTY_APPROVAL]: { accountTypes: ['admin'], tournamentRoles: ['Manager', 'Head Judge'] },
  [ROUTES.APPEALS]: { accountTypes: ['admin'], tournamentRoles: ['Manager', 'Head Judge'] },
  [ROUTES.APPEALS_LOG]: { accountTypes: ['admin'], tournamentRoles: ['Manager', 'Head Judge'] },
  [ROUTES.STAFF_MANAGEMENT]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager'] },
  [ROUTES.TOURNAMENT_SETUP]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Scorekeeper'] },
  [ROUTES.ARCHIVED_TOURNAMENTS]: { accountTypes: ['admin'], tournamentRoles: ['Manager'] },

  // Deck Check
  [ROUTES.DECK_CHECK]: { accountTypes: ['admin'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Deck Check', 'Feature Match'] },

  // Staff Dashboard & Management
  [ROUTES.STAFF_DASHBOARD]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.STAFF_SETTINGS]: { accountTypes: ['admin'], tournamentRoles: ['Manager'] },
  [ROUTES.STAFF_FLOOR_TEAMS]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.CHAT]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.STAFF_TOURNAMENT_ASSIGNMENTS]: { accountTypes: ['admin'], tournamentRoles: ['Manager'] },
  [ROUTES.STAFF_FEEDBACK]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.FEEDBACK_REPORTS]: { accountTypes: ['admin'] },

  // Tournament Engine access
  [ROUTES.TOURNAMENT_RUNNER]: { accountTypes: ['admin'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper', 'Team Lead'] },
  // Standings and bracket are viewable by all authenticated users
  [ROUTES.TOURNAMENT_STANDINGS]: { anyAuthenticated: true },
  [ROUTES.TOURNAMENT_BRACKET]: { anyAuthenticated: true },

  // Matchmaking runner: same staff tier as the tournament runner.
  [ROUTES.MATCHMAKING_RUNNER]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper', 'Team Lead'] },
  // Matchmaking dashboard: visible to the same crowd as TournamentDashboard
  // plus Scorekeepers (who need access to register walk-ups).
  [ROUTES.MATCHMAKING_DASHBOARD]: { accountTypes: ['admin', 'store', 'staff'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper'] },

  // DQ Cases - admin/store globally; Manager (or any tournament-staff
  // role assigned to a specific case) reaches it through the list view.
  [ROUTES.DQ_CASES]: { accountTypes: ['admin', 'store', 'staff'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'] },
  [ROUTES.DQ_CASE_NEW]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager'] },
  [ROUTES.DQ_CASE_DETAIL]: { accountTypes: ['admin', 'store', 'staff'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'] },

  // Streaming top-cut helper: same audience as the Top Cut Export
  // tab (managers / store / admin / Scorekeeper running broadcast).
  [ROUTES.STREAMING_TOP_CUT]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Head Judge', 'Scorekeeper'] },
  // Store managers — own-store delegation. Visible to admins
  // (for support) and store accounts (their own roster).
  [ROUTES.STORE_MANAGERS]: { accountTypes: ['admin', 'store'], tournamentRoles: [] },
  // STORE_INVITE_CLAIM is registered as a public auth route in
  // playerRoutes.ts — any logged-in user can land there to claim.

  // DQ_PARTICIPANT_FORM is registered as a public 'open' route in
  // publicRoutes.ts - no auth required, token in URL is the proof.

  // Tournament Series - admin or store can create + manage; managers
  // see them so they can attach a tournament to a series at creation
  // time.
  [ROUTES.TOURNAMENT_SERIES]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager'] },
  [ROUTES.TOURNAMENT_SERIES_NEW]: { accountTypes: ['admin', 'store'] },
  [ROUTES.TOURNAMENT_SERIES_DETAIL]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager'] },

  // Job Board
  // Note: STORE_APPLICATIONS also allows permanent managers (staff with permanentStoreId)
  // This is handled in the component level since route config doesn't support that check
  [ROUTES.JOB_BOARD]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.STORE_APPLICATIONS]: { accountTypes: ['admin', 'store', 'staff'] },
  [ROUTES.MY_APPLICATIONS]: { accountTypes: ['admin', 'store', 'staff'] },

  // Stream Overlay Manager (Stream role + admin + store owners)
  [ROUTES.STREAM_MANAGER]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Stream'] },
  [ROUTES.STREAM_OPERATOR]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Stream'] },
  [ROUTES.STREAM_CASTER]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Stream'] },
  [ROUTES.STREAM_SIMPLE]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Stream'] },
  [ROUTES.STREAM_EDITOR]: { accountTypes: ['admin', 'store'], tournamentRoles: ['Manager', 'Stream'] },
  // Overlay is public (OBS browser source)
  [ROUTES.STREAM_OVERLAY]: { anyAuthenticated: true },
};

// Legacy ROLE_ACCESS for backwards compatibility
export const ROLE_ACCESS: Record<string, UserRole[] | null> = {
  [ROUTES.ADMIN_DASHBOARD]: ['admin'],
  [ROUTES.ACCOUNTS]: ['admin'],
  [ROUTES.USER_APPROVAL]: ['admin', 'Manager'],
  [ROUTES.PLAYER_ACCOUNTS]: ['admin'],
  [ROUTES.RETENTION_POLICY]: ['admin'],
  [ROUTES.STORE_MANAGEMENT]: ['admin'],
  [ROUTES.EVENT_CODES]: ['admin'],
  [ROUTES.PENALTY_APPROVAL]: ['admin', 'Manager', 'Head Judge'],
  [ROUTES.APPEALS]: ['admin', 'Manager', 'Head Judge'],
  [ROUTES.APPEALS_LOG]: ['admin', 'Manager', 'Head Judge'],
  [ROUTES.STAFF_MANAGEMENT]: ['admin', 'Manager'],
  [ROUTES.TOURNAMENT_SETUP]: ['admin', 'Manager'],
  [ROUTES.ARCHIVED_TOURNAMENTS]: ['admin', 'Manager'],
  [ROUTES.DECK_CHECK]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Deck Check'],
  [ROUTES.STAFF_DASHBOARD]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
  [ROUTES.STAFF_SETTINGS]: ['admin', 'Manager'],
  [ROUTES.STAFF_FLOOR_TEAMS]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
  [ROUTES.CHAT]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead', 'Floor Judge', 'Feature Match', 'Shield', 'Deck Check'],
  [ROUTES.STAFF_TOURNAMENT_ASSIGNMENTS]: ['admin', 'Manager'],
  [ROUTES.TOURNAMENT_RUNNER]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead'],
  [ROUTES.TOURNAMENT_STANDINGS]: null,
  [ROUTES.TOURNAMENT_BRACKET]: null,
  [ROUTES.MATCHMAKING_RUNNER]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper', 'Team Lead'],
  [ROUTES.MATCHMAKING_DASHBOARD]: ['admin', 'Manager', 'Head Judge', 'Scorekeeper'],
};

// Check if user has access to route (new system)
export const hasRouteAccessNew = (
  route: string,
  accountType: AccountType | undefined,
  tournamentRole: TournamentRole | undefined
): boolean => {
  const access = ROUTE_ACCESS[route];

  // No specific access defined - default to staff access
  if (!access) {
    return accountType === 'admin' || accountType === 'staff' || accountType === 'store';
  }

  // Any authenticated user can access
  if (access.anyAuthenticated) {
    return true;
  }

  // Check account types
  if (access.accountTypes && accountType && access.accountTypes.includes(accountType)) {
    return true;
  }

  // Check tournament roles
  if (access.tournamentRoles && tournamentRole && access.tournamentRoles.includes(tournamentRole)) {
    return true;
  }

  return false;
};

// Legacy check (for backwards compatibility)
export const hasRouteAccess = (route: string, userRole: string | undefined): boolean => {
  const allowedRoles = ROLE_ACCESS[route];
  if (allowedRoles === null) return true; // Explicitly open to all authenticated
  if (!allowedRoles) return true; // No restrictions defined
  return allowedRoles.includes(userRole as UserRole);
};

export default ROUTES;
